import { onMounted } from 'vue';

import { registrationClient } from '@/api/registration';
import { readPersistedRegistration } from '@/composables/useRegistrationPersistence';
import { useRegistrationStore } from '@/stores/registration';
import type { RegistrationStateResponse, SubmittedRegistrationDraft } from '@/types/registration';

function isEmptyValue(value: unknown): boolean {
  if (value === '' || value === null || value === undefined) return true;
  return Array.isArray(value) && value.length === 0;
}

/**
 * Fetches the wizard-wide state and patches this step's draft section on every (re-)entry; steps remount
 * on forward, back and skip because `RegistrationPage` keys them on `store.currentStep.key`.
 *
 * A field is skipped from the patch when the backend has nothing for it while the draft does, or when the
 * registrant edited it since the fetch started. A session-storage draft for this step is authoritative, so
 * the whole section is left alone.
 *
 * @param stepKey which section of `RegistrationDraft` this step owns.
 * @param onSynced called after a successful fetch, even when nothing was patched, for steps needing more than the draft.
 */
export function useRegistrationStepSync<K extends keyof SubmittedRegistrationDraft>(
  stepKey: K,
  onSynced?: (state: RegistrationStateResponse, patchedFields: string[]) => void
) {
  const store = useRegistrationStore();

  async function sync(): Promise<RegistrationStateResponse | null> {
    const persisted = readPersistedRegistration();
    const localDraftIsAuthoritative = persisted?.step === stepKey;
    const before = JSON.parse(JSON.stringify(store.draft[stepKey])) as Record<string, unknown>;

    let state: RegistrationStateResponse;
    try {
      state = await registrationClient.getState();
    } catch {
      // request failed: leave the draft as it was
      return null;
    }

    store.applyServerState(state);

    const patchedFields: string[] = [];
    if (!localDraftIsAuthoritative) {
      const incoming = state.submitted[stepKey] as Record<string, unknown>;
      const section = store.draft[stepKey] as Record<string, unknown>;
      const patch: Record<string, unknown> = {};

      for (const field of Object.keys(section)) {
        const editedWhileLoading = JSON.stringify(section[field]) !== JSON.stringify(before[field]);
        if (!editedWhileLoading && !isEmptyValue(incoming[field])) {
          patch[field] = incoming[field];
          patchedFields.push(field);
        }
      }

      Object.assign(section, patch);
    }

    onSynced?.(state, patchedFields);
    return state;
  }

  onMounted(sync);

  return { sync };
}
